import {Observable, Observer, Subscription} from 'rxjs';

/**
 * RxJs pipeable operator.
 * Binds the source observable with the passed additional observables (additions),
 * so that subscription/unsubscription for the source will cause subscription/unsubscription for the additions.
 * Subscription to the additions goes after subscription to the source, and the reverse order for unsubscription.
 * Doesn't change any source's events, just mirrors them to the output.
 * The difference with {@link dependOn} is in the opposite subscription/unsubscription order.
 * @param {Array<Observable<*>>} additionalObservables
 * @returns {(source: Observable<T>) => Observable<T>}
 */
export const bindWith = <T>(...additionalObservables: Array<Observable<any>>) =>
    (source: Observable<T>): Observable<T> =>
        new Observable((observer: Observer<T>) => {
            const sourceSubscription = source.subscribe(observer);
            const additionalSubscription = additionalObservables.reduce(
                (subscription, dependency) => {
                    subscription.add(dependency.subscribe());

                    return subscription;
                },
                new Subscription()
            );

            return () => {
                additionalSubscription.unsubscribe();
                sourceSubscription.unsubscribe();
            };
        });
